import gql from 'graphql-tag';
import { Identifier } from 'ra-core';
import z from 'zod';

import { CustomerDataPackageRecord } from 'src/types';
import {
  ApiFieldParams,
  ApiFieldXrefParams,
  apiFieldsConfigSchema,
  resolveApiFieldsConfig,
} from 'src/utils/apiFieldsConfig';

export const customerDataPackageBaseSchema = z.object({
  name: z.string().trim().nonempty(),
});

export const customerDataPackageWithFieldsSchema = customerDataPackageBaseSchema.merge(apiFieldsConfigSchema);

type FormValues = z.infer<typeof customerDataPackageWithFieldsSchema>;

export function resolveMutationParams(values: FormValues, record: CustomerDataPackageRecord) {
  const customerDataPackageParams = { name: values.name };

  const { apiFieldsToAdd, apiFieldXrefsToUpdate, apiFieldXrefIdsToRemove } = resolveApiFieldsConfig(
    values,
    record?.api_fields_xrefs,
  );

  return resolveUpdateMutationParams(
    record.id,
    customerDataPackageParams,
    apiFieldsToAdd,
    apiFieldXrefsToUpdate,
    apiFieldXrefIdsToRemove,
  );
}

const genUpdateCustomerDataPackageGQL = (variableDeclarations: string[] = [], mutationSelections: string[] = []) => gql`
  mutation updateCustomerDataPackage(
    $customerDataPackageID: Int!,
    $customerDataPackageParams: customer_data_packages_set_input!,
    ${variableDeclarations.join('\n,')}
  ) {
    update_customer_data_packages(
      where: { id: { _eq: $customerDataPackageID } },
      _set: $customerDataPackageParams
    ) {
      affected_rows
    }
    ${mutationSelections.join('')}
  }
`;

export const updateCustomerDataPackageGQL = genUpdateCustomerDataPackageGQL();

const addApiFieldsFragment = /* GraphQL */ `
  insert_customer_data_packages_api_fields(objects: $newApiFields) {
    affected_rows
  }
`;

const updateApiFieldsFragment = /* GraphQL */ `
  update_customer_data_packages_api_fields_many(updates: $apiFieldUpdates) {
    affected_rows
  }
`;

const removeApiFieldsFragment = /* GraphQL */ `
  delete_customer_data_packages_api_fields(
    where: {
      customer_data_package_id: { _eq: $customerDataPackageID },
      id: { _in: $apiFieldXrefIdsToRemove }
    }
  ) {
    affected_rows
  }
`;

function resolveUpdateMutationParams(
  customerDataPackageID: Identifier,
  customerDataPackageParams: Record<string, unknown>,
  apiFieldsToAdd: ApiFieldParams[],
  apiFieldXrefsToUpdate: ApiFieldXrefParams[],
  apiFieldXrefIdsToRemove: number[],
) {
  const variableDeclarations = [];
  const mutationSelections = [];
  if (apiFieldsToAdd.length) {
    variableDeclarations.push('$newApiFields: [customer_data_packages_api_fields_insert_input!]!');
    mutationSelections.push(addApiFieldsFragment);
  }
  if (apiFieldXrefsToUpdate.length) {
    variableDeclarations.push('$apiFieldUpdates: [customer_data_packages_api_fields_updates!]!');
    mutationSelections.push(updateApiFieldsFragment);
  }
  if (apiFieldXrefIdsToRemove.length) {
    variableDeclarations.push('$apiFieldXrefIdsToRemove: [Int!]');
    mutationSelections.push(removeApiFieldsFragment);
  }

  const mutationDoc = genUpdateCustomerDataPackageGQL(variableDeclarations, mutationSelections);

  const newApiFields = apiFieldsToAdd.map((field) => ({
    customer_data_package_id: customerDataPackageID,
    api_field_id: field.api_field_id,
    policies: field.policies,
  }));

  const apiFieldUpdates = apiFieldXrefsToUpdate.map((fieldXref) => ({
    where: { id: { _eq: fieldXref.id }, customer_data_package_id: { _eq: customerDataPackageID } },
    _set: { policies: fieldXref.policies },
  }));

  return {
    mutation: mutationDoc,
    variables: {
      customerDataPackageID,
      customerDataPackageParams,
      ...(newApiFields.length && { newApiFields }),
      ...(apiFieldUpdates.length && { apiFieldUpdates }),
      ...(apiFieldXrefIdsToRemove.length && { apiFieldXrefIdsToRemove }),
    },
  };
}
